import {
  AnalyticsProperties,
  CommonAnalyticsV2,
  GCInstantError,
  InviteAsyncOptions,
  LineShareTargetPickerPlugin,
} from '@play-co/gcinstant';
import QRCode from 'qrcode';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { CreativeAsset } from '../creatives/core';
import { CreativeText } from '../replicant/creatives/text';
import { analytics, makePayload } from './analytics';
import StateObserver, { user } from '../StateObserver';
import { captureGenericError } from './sentry';
import { getBaseMiniAppURL } from '../replicant/utils/payload';
import { isSupportedMobilePlatform } from 'src/game/native/native.util';
import { openPopupPromise } from './popups/popupOpenClose';
import { CardId } from 'src/replicant/cards/cards.ruleset';
import { NativeShareCreativeID } from 'src/creatives/share';
import { getShareLinkCache } from 'src/replicant/social/social.getters';
import { track } from './analytics/utils';
import { Feature } from 'src/replicant/analytics';
import { getCreativeText } from 'src/creatives/text';
import {
  getRandomInviteAsset,
  inviteMessageWithCTA,
  inviteMessageWithoutCTA,
} from 'src/replicant/chatbot/flex/invite';
import type { DeepLink } from 'src/game/launch/deepLinks';
import { FlexCreativeAsset } from 'src/replicant/chatbot/flex/utils';
import { FlexMessage } from 'src/replicant/chatbot/flex/types';

export type CardShareKind = 'request' | 'donate' | 'trade';

type CardSharePayload = {
  cardId?: CardId;
  cardShareKind?: CardShareKind;
  cardShareCreatedAt?: number;
};

type JankenPayload = {
  challengeId?: string;
  senderSpecies?: string;
  senderDisplayName?: string;
};

type InviteOpts = {
  creativeAsset?: CreativeAsset;
  creativeText?: CreativeText;
  userAsset?: boolean; // Whether the creative asset is a user asset that needs to be uploaded to S3 for link preview (default: false)
  text?: string;
  cta?: string;

  data: {
    feature: string;
    $subFeature: string;
    originFeature?: string;
    originSubFeature?: string;
    $sharingID?: string;
    $sharingTimestamp?: string;
    rewardOnConversion?: boolean;
    $deepLink?: DeepLink;
  } & CardSharePayload &
    JankenPayload;
};

/**
 * TODO: Remove — inviteAsync is a no-op on PlatformWeb (always returns void), so this
 * wrapper always returns []. The entire invite flow through this function is dead code
 * after PlatformLine removal.
 */
export async function inviteAsync(
  args: InviteOpts & {
    receiverIDs?: string[];
    sortedIDs?: string[];
    confirmPopup?: boolean;
    debug?: boolean;
  },
): Promise<string[]> {
  const opts: InviteAsyncOptions = {
    image: args.creativeAsset?.image ?? '',
    text: args.text || args.creativeText?.text || '',
    cta: args.cta || args.creativeText?.cta,

    data: {
      ...args.data,
      selectionCount: args.receiverIDs?.length,
      ...makePayload('INVITE'),

      $creativeAssetID: args.creativeAsset?.id,
      $creativeTextID: args.creativeText?.id,
    },
  };

  await platform.inviteAsync(opts);

  // TODO: inviteAsync returns void on PlatformWeb — the receiver tracking below is unreachable.
  // Remove this dead code along with the rest of the dead invite flow.
  return [];
}

export async function generateInviteURL(args: InviteOpts) {
  const state = user();
  const now = StateObserver.now();

  const opts: InviteAsyncOptions = {
    image: args.creativeAsset?.image ?? '',
    text: args.text || args.creativeText?.text || '',
    cta: args.cta || args.creativeText?.cta,

    data: {
      ...args.data,
      ...makePayload('SHARE'),
      $creativeAssetID: args.creativeAsset?.id,
      $creativeTextID: args.creativeText?.id,
      $creativeCTA: args.creativeText?.cta,
      webReferrer: user().id,
    },
  };

  // Get the description.
  const description =
    typeof opts.text === 'string' ? opts.text : opts.text.default;

  // Use saved link if it's still valid.
  const { hash, linkId } = getShareLinkCache(state, opts, now);
  if (linkId) {
    return {
      inviteUrl: `https://playco.click/${linkId}`,
      description,
    };
  }

  // Generate new link
  const { invitePayload } = encodeInvitePayload(
    opts.data,
    'SHARE',
    'invite_url',
  );

  try {
    const linkId = await StateObserver.invoke.generateWebShareLink({
      title: process.env.LINE_APP_NAME!,
      assetId: args.creativeAsset!.id as NativeShareCreativeID,
      image: args.userAsset
        ? args.creativeAsset!.image
        : StateObserver.replicant.getChatbotAssetUrl(args.creativeAsset!.image),
      payload: invitePayload,
      hash,
    });

    // Capture error if link generation failed and return null
    if (!linkId) {
      throw new Error('Link was not generated');
    }

    return {
      inviteUrl: `https://playco.click/${linkId}`,
      description,
    };
  } catch (e) {
    // Also capture any other unexpected errors
    captureGenericError('generateInviteURLFailed', e);
    return null;
  }
}

/**
 * Generates a direct LINE Mini App URL with encoded payload,
 * bypassing the playco.click shortlink service.
 *
 * @example
 * const url = generateDirectMiniAppURL({
 *   data: {
 *     feature: Feature.INVITE._,
 *     $subFeature: Feature.INVITE.NATIVE_SHARE,
 *   },
 * });
 * // Returns: "https://miniapp.line.me/2007326019-vAg8J6Bp?payload=..."
 */
export function generateDirectMiniAppURL(args: InviteOpts): string | undefined {
  try {
    // Build full payload data - all properties are encoded into the $key by encodeInvitePayload,
    // which stores them server-side. The recipient retrieves the full data via the $key lookup.
    // We only include $key in the URL to keep the QR code simple (fewer modules = easier to scan).
    const data = {
      ...args.data,
      ...makePayload('SHARE'),
      $creativeAssetID: args.creativeAsset?.id,
      $creativeTextID: args.creativeText?.id,
      webReferrer: user().id,
    };

    const { invitePayload } = encodeInvitePayload(data, 'SHARE', 'invite_url');

    // Only include $key in URL - full payload is retrieved server-side via key lookup
    const url = new URL(getBaseMiniAppURL());
    url.searchParams.set(
      'payload',
      JSON.stringify({ $key: invitePayload['$key'] }),
    );

    return url.toString();
  } catch (e) {
    captureGenericError('generateDirectMiniAppURLFailed', e);
    return undefined;
  }
}

type InviteURLMethod = 'navigator-share' | 'line-share' | 'copy';

export async function sendInviteURL({
  inviteUrl,
  description,
  fallbackPopupOpts,
}: {
  inviteUrl: string;
  description: string;
  fallbackPopupOpts?: {
    title: string;
    message: string;
  };
}): Promise<InviteURLMethod> {
  // If the native share API is available and we're not in LIFF, use it.
  if (navigator.share && !window.liff?.isInClient()) {
    const shareData = {
      title: process.env.LINE_APP_NAME!,
      text: description,
      url: inviteUrl,
    };

    // Check if the native share API is available
    if (navigator.canShare(shareData)) {
      navigator.share(shareData).catch((e) => {
        // If the user cancels the share, don't log an error.
        if (e.name === 'AbortError') return;

        // Otherwise log the error.
        captureGenericError('Unable to share via native share API', e, {
          extra: shareData,
        });
        analytics.pushError('SendInviteURLFailed', e, shareData);
      });

      // Only return if `canShare` is true, otherwise fall back to the next method.
      return 'navigator-share';
    }
  }

  // Otherwise, if the LINE app is available, use its share API.
  if (isSupportedMobilePlatform()) {
    const inviteMessage = `${description ?? ''}\n${inviteUrl}`;
    const link =
      'https://line.me/R/share?text=' + encodeURIComponent(inviteMessage);

    // On Android we should open the link in a new tab.
    if (platform.osType?.includes('ANDROID')) {
      window.open(link);
    }
    // Otherwise use the same window.
    else {
      window.location.assign(link);
    }
    return 'line-share';
  }

  // Copy the URL to the clipboard.
  navigator.clipboard.writeText(inviteUrl);
  track('UrlCopied', {});

  if (!fallbackPopupOpts) {
    return 'copy';
  }

  // Otherwise, open a popup letting the user know the URL has been copied.
  await openPopupPromise('popupInviteURLCopied', {
    url: inviteUrl,
    ...fallbackPopupOpts,
  });

  return 'copy';
}

const shareTargetPicker = new LineShareTargetPickerPlugin();
platform.addPlugin(shareTargetPicker);

export function isShareTargetPickerAvailable() {
  return shareTargetPicker.isAvailable();
}

export async function sendInviteShareTargetPicker() {
  if (!isShareTargetPickerAvailable()) {
    console.warn(
      'sendInviteShareTargetPicker: LINE shareTargetPicker is not available',
    );
    return;
  }

  const creativeAsset = getRandomShareTargetPickerCreative();
  const creativeText = getCreativeText('invite');

  const { encodedPayload, analyticsEventProperties } =
    platform.generateEntryPayload({
      ...makePayload('SHARE'),
      $creativeAssetID: creativeAsset.id,
      $creativeTextID: creativeText.id,
      $creativeCTA: creativeText.cta,
      creativeCTA: creativeText.cta,
      channel: 'SHARE',
      feature: Feature.INVITE._,
      subFeature: Feature.INVITE.LINE_SHARE,
      $subFeature: Feature.INVITE.LINE_SHARE,
      originFeature: Feature.INVITE._,
      originSubFeature: Feature.INVITE.LINE_SHARE,
      webReferrer: user().id,
    });

  const flexMessage = createInviteShareTargetPickerFlexMessage({
    creativeAsset,
    creativeText,
    payload: encodedPayload,
  });

  return openShareTargetPicker(flexMessage, analyticsEventProperties);
}

export async function openShareTargetPicker(
  flexMessage: FlexMessage,
  analyticsEventProperties: CommonAnalyticsV2,
) {
  try {
    await shareTargetPicker.open([flexMessage], { analyticsEventProperties });
  } catch (e) {
    if (!isUserInputError(e)) {
      captureGenericError('shareTargetPickerFailed', e);
    }
  }
}

export function encodeInvitePayload(
  data: InviteAsyncOptions['data'],
  channel: AnalyticsProperties.Channel,
  selectionUI?: string,
) {
  // Dynamic property access to use the protected function safely.
  const payload = {
    ...platform['_populateNotificationPayload'](data, channel),
    selectionUI,
  };

  const analyticsPayload = {};
  excludePayloadOnlyProperties({
    ...payload,
    platformAPI: 'InviteAsync' as const,
  });

  return {
    invitePayload: platform['dataCodec'].encode(payload),
    analyticsPayload,
  };
}

/** @returns `payload` without the properties that are not social event properties. */
function excludePayloadOnlyProperties<T extends AnalyticsProperties.Payload>(
  payload: T,
): Omit<T, AnalyticsProperties.PayloadOnlyProperties> {
  const { sourceChainTimestamps, tournamentAPI, ...rest } = payload;

  return rest;
}

/**
 * Determines whether an unknown error value represents a user input error.
 */
function isUserInputError(error: unknown): boolean {
  if (error instanceof GCInstantError && error.code === 'USER_INPUT') {
    return true;
  }

  return false;
}

/**
 * Create a static flex message for invite sharing via LINE share target picker
 */
function createInviteShareTargetPickerFlexMessage(args: {
  creativeAsset: FlexCreativeAsset;
  creativeText: CreativeText;
  payload: object;
}) {
  const title = process.env.LINE_APP_NAME || 'ぶるぶるどーぶつ';
  const options = {
    title,
    asset: args.creativeAsset,
    creative: args.creativeText,
    payload: {
      ...args.payload,
      alternativeWebIABBucket: 'enabled_mini_app' as const, // Always open in mini app
    },
  };

  if (args.creativeAsset.noCTA) {
    return inviteMessageWithoutCTA(options);
  }

  return inviteMessageWithCTA(options);
}

/**
 * Get random creative asset for share target picker
 */
function getRandomShareTargetPickerCreative(): FlexCreativeAsset {
  const creative = getRandomInviteAsset(Math.random); // It's frontend only code so we can use Math.random
  creative.image = StateObserver.replicant.getChatbotAssetUrl(creative.image);
  return creative;
}

/**
 * Generate a QR code as a data URL from a given URL.
 *
 * @param url - The URL to encode in the QR code
 * @param options - Optional QR code options
 * @returns A promise that resolves to the QR code as a base64 data URL
 */
export async function generateQRCode(
  url: string,
  options?: {
    width?: number;
    margin?: number;
    color?: {
      dark?: string;
      light?: string;
    };
  },
): Promise<string | undefined> {
  try {
    const dataUrl = await QRCode.toDataURL(url, {
      width: options?.width ?? 256,
      margin: options?.margin ?? 2,
      scale: 1,
      color: {
        dark: options?.color?.dark ?? '#000000',
        light: options?.color?.light ?? '#FFFFFF',
      },
      errorCorrectionLevel: 'L',
    });

    return dataUrl;
  } catch (error) {
    captureGenericError('generateQRCodeFailed', error);
    return undefined;
  }
}
